import { getFileUrl } from '@/clients/http'
import { BrandModel as BrandApi } from '@/http/models/brand/Brand'
import { Image as ImageIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useEffect, useState } from 'react'

import { useEnterSubmit } from '@/hooks/ui'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { SwitchInput, TextInput } from '@/components/atoms'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { BrandModel } from '@/types'
import { Spinner } from '@/components/ui/spinner'

interface BrandDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editBrand: BrandModel.Brand | null
  closeDialog: () => void
  onCreate: (data: BrandFormData) => void
  onUpdate: (id: string, data: BrandFormData) => void
  loading?: boolean
}

interface BrandFormData {
  name: string
  description: string
  status: 'active' | 'inactive'
  image_url_temp?: string | File
  logo_id?: string
}

export const BrandDialog = ({
  open,
  mode,
  editBrand,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: BrandDialogProps) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<BrandFormData>({
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      image_url_temp: '',
      logo_id: '',
    },
  })

  const [galleryOpen, setGalleryOpen] = useState(false)
  const formValues = watch()

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '')
  }

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editBrand) {
      reset({
        name: editBrand.name,
        description: editBrand.description,
        status: editBrand.status,
        image_url_temp: editBrand?.logo?.path,
        logo_id: editBrand?.logo?._id,
      })
    } else {
      reset({
        name: '',
        description: '',
        status: 'active',
        image_url_temp: '',
        logo_id: '',
      })
    }
  }, [mode, editBrand, reset])

  const onSubmit = async (data: BrandFormData) => {
    if (!data.image_url_temp) {
      delete data.image_url_temp
    }
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editBrand?._id) return
      onUpdate(editBrand._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add brand' : mode === 'edit' ? 'Edit brand' : 'Brand details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new product brand.'
              : mode === 'edit'
                ? 'Update brand information.'
                : 'View brand details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-4">
            <TextInput
              control={control}
              name="name"
              label="Name"
              placeholder="Apple"
              disabled={mode === 'view'}
              rules={{ required: 'Name is required' }}
              errors={errors}
            />

            <TextInput
              control={control}
              name="description"
              label="Description"
              placeholder="Brand description"
              disabled={mode === 'view'}
              multiline
              rows={4}
              errors={errors}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <span className="text-sm text-ink">Logo</span>

              <div className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-subdued p-4">
                {formValues?.image_url_temp ? (
                  <div className="group relative aspect-video w-full overflow-hidden rounded-md md:aspect-auto md:h-44">
                    <img
                      src={getFileUrl(formValues?.image_url_temp as string)}
                      alt="Brand"
                      className="w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-ink/50 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setGalleryOpen(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setValue('image_url_temp', '')
                          setValue('logo_id', '')
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <ImageIcon
                      className="mx-auto size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-muted-foreground">No image selected</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setGalleryOpen(true)}
                      disabled={mode === 'view'}
                    >
                      Select image
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <SwitchInput
              control={control}
              name="status"
              label="Status"
              description={formValues?.status === 'active' ? 'Brand is visible' : 'Brand is hidden'}
              disabled={mode === 'view'}
              activeValue="active"
              inactiveValue="inactive"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" onClick={handleSubmit(onSubmit)} disabled={loading}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('image_url_temp', files[0].url)
            setValue('logo_id', files[0].id)
          }
        }}
      />
    </Dialog>
  )
}
